import { ArrowDownLeft, ArrowUpRight, ExternalLink } from 'lucide-react'
import { Link } from 'react-router'

import { KeyValueList, Money, StatusBadge } from '@/components/ds'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet'

import { BankAccount, BankAccountTransaction, docPath, docTypeLabel, formatTxDate, MatchedDoc, txAmount, txTitle } from './bankUtils'

const docTotal = (m: MatchedDoc) => (m.doc.items ?? []).reduce((s, it) => s + Number(it.totalPrice?.amount ?? 0), 0)
const docCurrency = (m: MatchedDoc) => (m.doc as { currency?: string }).currency ?? m.doc.items?.[0]?.totalPrice?.currency ?? 'CZK'

interface TransactionSheetProps {
  transaction: BankAccountTransaction | null
  match: MatchedDoc | null
  account?: BankAccount
  onOpenChange: (open: boolean) => void
}

/** Detail transakce v pravém panelu (Mercury). */
export const TransactionSheet = ({ transaction: t, match, account, onOpenChange }: TransactionSheetProps) => {
  if (!t) return <Sheet open={false} onOpenChange={onOpenChange} />

  const amount = txAmount(t)
  const incoming = amount > 0
  const counterparty = match ? (match.type === 'expense' ? match.doc.supplier : match.doc.customer) : null

  return (
    <Sheet open={!!t} onOpenChange={onOpenChange}>
      <SheetContent className="overflow-y-auto" onOpenAutoFocus={(e) => e.preventDefault()}>
        <div className="flex items-center gap-3 border-b px-6 py-4 pr-12">
          <span className="grid size-7 place-items-center rounded-full bg-muted text-muted-foreground" aria-hidden>
            {incoming ? <ArrowDownLeft className="size-4" /> : <ArrowUpRight className="size-4" />}
          </span>
          <SheetTitle className="text-sm font-medium">{incoming ? 'Příchozí platba' : 'Odchozí platba'}</SheetTitle>
          <StatusBadge status={match ? 'matched' : 'unmatched'} label={match ? 'Spárováno' : 'Nespárováno'} />
        </div>
        <SheetDescription className="sr-only">Detail bankovní transakce</SheetDescription>

        <div className="space-y-6 px-6 py-6">
          <div className="space-y-2">
            <p className="truncate text-sm text-muted-foreground">{txTitle(t)}</p>
            <Money amount={amount} currency={t.amount?.currency} size="xl" signed tone />
            <p className="text-sm text-muted-foreground tabular">{formatTxDate(t.date)}</p>
          </div>

          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Párování</p>
            {match ? (
              <Link
                to={docPath(match)}
                className="flex items-center justify-between gap-4 rounded-lg border px-4 py-3 transition-colors hover:bg-muted"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium tabular">
                    {docTypeLabel[match.type]} {match.doc.symbol}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">{counterparty?.name ?? '-'}</p>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  <Money amount={docTotal(match)} currency={docCurrency(match)} size="sm" />
                  <ExternalLink className="size-4 text-muted-foreground" />
                </div>
              </Link>
            ) : (
              <div className="rounded-lg bg-muted px-4 py-3 text-sm">
                <p>K platbě jsme nenašli doklad.</p>
                <p className="mt-1 text-muted-foreground text-pretty">
                  {!incoming
                    ? 'Odchozí platby se s náklady automaticky nepárují.'
                    : t.vs
                      ? `Variabilní symbol ${t.vs} neodpovídá žádné faktuře ani výzvě k platbě.`
                      : 'Platba nemá variabilní symbol. Párujeme podle variabilního symbolu nebo čísla dokladu.'}
                </p>
                {incoming && (
                  <Button variant="link" asChild className="mt-2">
                    <Link to="/invoices?status=open">Zobrazit neuhrazené faktury</Link>
                  </Button>
                )}
              </div>
            )}
          </div>

          <KeyValueList
            items={[
              { label: 'Datum', value: formatTxDate(t.date) },
              ...(t.payer ? [{ label: 'Plátce', value: t.payer }] : []),
              { label: 'Variabilní symbol', value: t.vs || '-' },
              ...(t.ss ? [{ label: 'Specifický symbol', value: t.ss }] : []),
              ...(t.ks ? [{ label: 'Konstantní symbol', value: t.ks }] : []),
              ...(account ? [{ label: 'Účet', value: `${account.name}, ${account.bankAccount.accountNumber}/${account.bankAccount.bankCode}` }] : []),
              ...(t.externalId ? [{ label: 'ID v bance', value: t.externalId }] : []),
            ]}
          />

          {(t.message || t.comment || t.note) && (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">Zpráva a poznámky</p>
              <dl className="divide-y border-y text-sm">
                {[
                  { label: 'Zpráva pro příjemce', value: t.message },
                  { label: 'Komentář', value: t.comment !== t.message ? t.comment : null },
                  { label: 'Poznámka', value: t.note },
                ]
                  .filter((r) => r.value)
                  .map((r) => (
                    <div key={r.label} className="space-y-0.5 py-2.5">
                      <dt className="text-xs text-muted-foreground">{r.label}</dt>
                      <dd className="whitespace-pre-wrap break-words">{r.value}</dd>
                    </div>
                  ))}
              </dl>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
